import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { ArrowLeft, BarChart3, AlertCircle, Loader } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface SurveyComponent {
  id: string;
  type: string;
  settings: {
    title: string;
    description: string;
    questions: Array<{
      id: string;
      question: string;
      type: string;
    }>;
  };
}

interface PageData {
  id: string;
  name: string;
  components: SurveyComponent[];
}

export const SurveyList: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const [page, setPage] = useState<PageData | null>(null);
  const [surveys, setSurveys] = useState<SurveyComponent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responseCount, setResponseCount] = useState<Record<string, number>>({});

  useEffect(() => {
    if (pageId) {
      loadSurveys();
    }
  }, [pageId, token]);

  const loadSurveys = async () => {
    try {
      setIsLoading(true);
      
      // Fetch page data
      const response = await fetch(`${API_URL}/pages/${pageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load page');
      }

      const pageData = await response.json();
      setPage(pageData);

      // Filter only survey components
      const surveyComponents = pageData.components.filter(
        (comp: any) => comp.type === 'survey'
      );
      setSurveys(surveyComponents);

      // Fetch response counts for each survey
      const counts: Record<string, number> = {};
      for (const survey of surveyComponents) {
        try {
          const countResponse = await fetch(`${API_URL}/surveys/${pageId}/${survey.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (countResponse.ok) {
            const data = await countResponse.json();
            counts[survey.id] = data.totalResponses || 0;
          }
        } catch (err) {
          console.error(`Failed to fetch count for survey ${survey.id}:`, err);
          counts[survey.id] = 0;
        }
      }
      setResponseCount(counts);

    } catch (error) {
      console.error('Load surveys error:', error);
      toast.error('Failed to load surveys');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </UserLayout>
    );
  }

  if (!page) {
    return (
      <UserLayout>
        <div className="p-4 sm:p-6 lg:p-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Survey List</h1>
          <p className="text-gray-600">Could not load page. Please try again.</p>
          <button onClick={() => navigate('/dashboard')} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg">
            Go to Dashboard
          </button>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">📊 Survey Results</h1>
          <p className="text-gray-600 mt-1">
            Page: <span className="font-semibold">{page.name}</span>
          </p>
        </div>

        {/* Surveys Grid */}
        {surveys.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <AlertCircle size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No surveys found</h3>
            <p className="text-gray-600 mb-6">This page doesn't have any survey components yet.</p>
            <button
              onClick={() => {
                navigate('/builder');
              }}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Go to Builder
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {surveys.map((survey) => (
              <div
                key={survey.id}
                onClick={() => navigate(`/survey-results/${pageId}/${survey.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-xl hover:border-purple-300 transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                        {survey.settings.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {survey.settings.questions.length} question{survey.settings.questions.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                </div>

                {survey.settings.description && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {survey.settings.description}
                  </p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-600">Responses</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {responseCount[survey.id] || 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Questions</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {survey.settings.questions.length}
                    </p>
                  </div>
                </div>

                {/* View Button */}
                <button className="w-full py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-medium transition-all group-hover:shadow-lg">
                  View Results →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </UserLayout>
  );
};

